import React, { useState, useRef } from 'react';
import { SubjectProps } from './SubjectInterface';
import { FiMoreVertical, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { convertDate, ucFirst } from '../../utils/Helper';
import './Subject.style.scss';
import { ConfirmDeleteToast } from '../../utils/Helper';
import { toast } from 'react-hot-toast';
import { taxonomyItemNames } from '../../utils/subjectTaxonomy';
import { DEFAULT_LEVEL_LABELS } from './detail/hierarchyUtils';

interface SubjectCardProps {
  data: SubjectProps;
  index: number;
  openMenuIndex: number | null;
  setOpenMenuIndex: React.Dispatch<React.SetStateAction<number | null>>;
  deleteSubject: (subjectId: string | number) => void;
  onSubjectUpdate: any;
}

const pluralize = (label: string, count: number) =>
  `${count} ${label}${count === 1 ? '' : 's'}`;

export const SubjectCard: React.FC<SubjectCardProps> = ({
  data,
  index,
  openMenuIndex,
  setOpenMenuIndex,
  deleteSubject,
}) => {
  const navigate = useNavigate();
  const subjectName = data && data.title ? ucFirst(data.title) : '';
  const updateDate = data.updated_at;
  const firstTeacherName =
    data && Array.isArray(data.teachers) && data.teachers.length > 0
      ? `${ucFirst(data.teachers[0].first_name)} ${ucFirst(data.teachers[0].last_name)}`
      : 'N/A';

  const labels =
    data.level_labels && data.level_labels.length
      ? data.level_labels
      : DEFAULT_LEVEL_LABELS;

  const topLevelNames = taxonomyItemNames(
    Array.isArray(data.topics) ? data.topics : []
  );
  const topLevelCount = topLevelNames.length;

  const [showTopic, setShowTopic] = useState(false);
  const [topicPosition, setTopicPosition] = useState({ top: 0, left: 200 });

  const chaptersRef = useRef<HTMLParagraphElement | null>(null);

  const goToDetail = () => {
    if (data.id !== undefined) {
      navigate(`/subjects/${data.id}`);
    }
  };

  const handleMouseEnter = () => {
    if (chaptersRef.current) {
      const rect = chaptersRef.current.getBoundingClientRect();
      setTopicPosition({
        top: rect.top + window.scrollY + -180,
        left: rect.left + rect.width + -10,
      });
    }
    setShowTopic(true);
  };

  const handleMouseLeave = () => setShowTopic(false);

  const handleToggleMenu = () => {
    setOpenMenuIndex(prevIndex => (prevIndex === index ? null : index));
  };

  const handleDeleteClick = () => {
    if (data.id === undefined) {
      console.error('ID is undefined');
      return;
    }
    setOpenMenuIndex(null);
    toast(t => (
      <ConfirmDeleteToast
        onConfirm={() => {
          deleteSubject(data.id as string | number);
          toast.dismiss(t.id);
        }}
        onCancel={() => toast.dismiss(t.id)}
        message={`Are you sure you want to delete ${subjectName}?`}
      />
    ));
  };

  return (
    <>
      <div className="profile_card" key={`subject_card_${index}_${data.id}`}>
        <div className="action_menu">
          <FiMoreVertical className="cursor-pointer" onClick={handleToggleMenu} />
        </div>
        <div className="profile_image">
          {data.image ? (
            <img src={data.image} alt={subjectName} height={100} width={100} />
          ) : (
            <> </>
          )}
        </div>
        <div className="profile_info">
          <div className="name_subject">
            <h2 className="cursor-pointer" onClick={goToDetail}>
              {subjectName}
            </h2>
            <p>{firstTeacherName}</p>
          </div>

          {openMenuIndex === index && (
            <div className="dropdown-menu">
              <ul>
                <li className="cursor-pointer" onClick={goToDetail}>
                  View
                </li>
                <li className="cursor-pointer" onClick={goToDetail}>
                  Edit
                </li>
                <li className="cursor-pointer" onClick={handleDeleteClick}>
                  Delete
                </li>
              </ul>
            </div>
          )}

          <div className="subject_card_middle_content">
            <p className="last_updated">
              Last updated: {updateDate ? convertDate(updateDate) : ''}
            </p>
            <p
              className="chapters"
              ref={chaptersRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {pluralize(labels[0], topLevelCount)}
            </p>
          </div>
        </div>
        <div className="students">
          <div className="students_image">
            {Array.isArray(data.teachers) &&
              data.teachers.slice(0, 3).map(teacher => (
                <div className="student" key={teacher.id}>
                  <img
                    src={teacher.profile_photo || 'default-profile.png'}
                    alt={`${teacher.first_name || 'Student'}`}
                  />
                </div>
              ))}
            {data.teachers && data.teachers.length > 3 && (
              <div className="student_count">
                {'+' + (data.teachers.length - 3)}
              </div>
            )}
          </div>

          <p>
            {firstTeacherName}
            {data.teachers.length > 1 && (
              <span>
                {' '}
                and {data.teachers.length - 1} other teacher
                {data.teachers.length - 1 > 1 && 's'}{' '}
              </span>
            )}
          </p>
        </div>
      </div>
      {showTopic && (
        <div
          className="topic_contain"
          style={{
            position: 'absolute',
            top: topicPosition.top,
            left: topicPosition.left,
            padding: '1em',
            zIndex: 999,
            borderRadius: '10px',
            background: '#FFF',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div>
            <div className="flex flex-col items-center text-center max-w-sm mx-auto">
              <FiBookOpen className="text-2xl mb-2" />
              <h2 className="font-semibold text-gray-800 mb-2">{subjectName}</h2>
              <h3 className="text-md text-gray-600">
                {pluralize(labels[0], topLevelCount)}
              </h3>
            </div>
            {topLevelCount > 0 && (
              <>
                <p className="text-sm font-medium text-gray-700 mt-2">
                  {labels[0]}
                </p>
                <ol className="space-y-1 list-decimal pl-5">
                  {topLevelNames.map((name, idx) => (
                    <li key={`top-${idx}`}>
                      <p>{name}</p>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
